import type { IconName } from '@customerio/pluma-icons';
import { defaultFilter } from 'cmdk';
import type { LocalCommand } from './local-commands';
import type {
  SearchResult,
  TemplateType,
  CustomerResult,
  SourceResult,
  DestinationResult,
} from '@/services/auth/types';
import type { ServicesApiCustomer, ServicesApiPipelinesConnection } from '@/services/api/services-api';
import { resolveAssetUrl } from '@/utils/resolve-asset-url';
import { ACTION_TYPE_ICONS, TEMPLATE_TYPE_ICONS, CAMPAIGN_TYPE_ICONS, type GroupType } from './constants';
import { isEmptyString } from '@/utils/string';
import { z } from '@customerio/hydra-validation';

type CatalogSourceEntry = { option_id: string; name: string; icon: string; category: string };
type CatalogDestinationEntry = CatalogSourceEntry & { mode?: string };

export type IntegrationCatalog = Map<CatalogDestinationEntry['option_id'], Omit<CatalogDestinationEntry, 'option_id'>>;

export function buildIntegrationCatalog(
  sources: CatalogSourceEntry[],
  destinations: CatalogDestinationEntry[],
): IntegrationCatalog {
  const map: IntegrationCatalog = new Map();
  for (const opt of sources) {
    map.set(opt.option_id, { name: opt.name, icon: opt.icon, category: opt.category });
  }
  for (const opt of destinations) {
    map.set(opt.option_id, { name: opt.name, icon: opt.icon, category: opt.category, mode: opt.mode });
  }
  return map;
}

export function getIntegrationTypeName(type: string, catalog: IntegrationCatalog): string | undefined {
  return catalog.get(type)?.name;
}

export function getIntegrationIconUrl(type: string, catalog: IntegrationCatalog): string | undefined {
  const entry = catalog.get(type);
  return entry != null ? resolveAssetUrl(entry.icon) : undefined;
}

/**
 * Fuzzy-matches connections against the query by title, type slug, and catalog display name,
 * then caps the result set. Capping after filtering avoids hiding later-indexed connections
 * (e.g. destinations on workspaces with many sources, since the API returns connections
 * grouped by direction).
 */
export function filterAndCapIntegrations(
  connections: readonly (SourceResult | DestinationResult)[],
  query: string,
  catalog: IntegrationCatalog,
  cap: number,
): (SourceResult | DestinationResult)[] {
  if (query.length === 0) return [];
  const q = query.toLowerCase();
  const matches: (SourceResult | DestinationResult)[] = [];
  for (const r of connections) {
    const displayName = getIntegrationTypeName(r.other.type, catalog);
    if ([r.title, r.other.type, displayName ?? ''].some((s) => defaultFilter(s.toLowerCase(), q) > 0)) {
      matches.push(r);
      if (matches.length >= cap) break;
    }
  }
  return matches;
}

/**
 * Maps a ServicesApiCustomer to a CustomerResult for use in the command dialog.
 *
 * Title resolution order: name → email → "Person with id {id}" → "cio_id: {cio_id}"
 *
 * The `_created_in_customerio_at` attribute is a Unix timestamp (seconds) stored as a string.
 * It is converted to an ISO 8601 string for display; invalid or missing values are omitted.
 */
export function mapCustomerToSearchResult(customer: ServicesApiCustomer, workspaceId: number): CustomerResult {
  const cioCreatedAt = customer.attributes?.['_created_in_customerio_at'] as string | undefined;
  const parsedCreatedAt =
    cioCreatedAt != null && !isEmptyString(cioCreatedAt) ? Number.parseInt(cioCreatedAt, 10) : Number.NaN;
  const formattedCreatedAt = !Number.isNaN(parsedCreatedAt)
    ? new Date(parsedCreatedAt * 1000).toISOString()
    : undefined;

  const name = customer.attributes['name'];
  const email = customer.attributes['email'];
  const nameString = typeof name === 'string' && !isEmptyString(name) ? name : undefined;
  const emailString = typeof email === 'string' && !isEmptyString(email) ? email : undefined;
  const title =
    nameString ??
    emailString ??
    (customer.id !== undefined ? `Person with id ${customer.id}` : `cio_id: ${customer.cio_id}`);

  return {
    kind: 'customer',
    doc_id: customer.id ?? customer.cio_id,
    title,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/people/${customer.id ?? customer.cio_id}`,
    other: {
      id: customer.id,
      email: emailString,
      unsubscribed: customer.unsubscribed,
      created_at: formattedCreatedAt,
    },
  };
}

/**
 * Maps a ServicesApiPipelinesConnection to a SourceResult or DestinationResult.
 * The `path` field from the API is used as the navigation URL.
 */
export function mapPipelinesConnectionToSearchResult(
  conn: ServicesApiPipelinesConnection,
): SourceResult | DestinationResult {
  const other = {
    type: conn.type,
    enabled: conn.enabled,
    status: conn.status,
    created_at: conn.created_at > 0 ? conn.created_at : undefined,
    last_activity: conn.last_activity > 0 ? conn.last_activity : undefined,
  };
  return conn.direction === 'Source'
    ? { kind: 'source', doc_id: conn.id, title: conn.name, url: conn.path, other }
    : { kind: 'destination', doc_id: conn.id, title: conn.name, url: conn.path, other };
}

/**
 * Builds the query string for the people (customers) endpoint.
 * The endpoint supports wildcard searches — if the input already contains '@' (email/domain)
 * or '*' (explicit wildcard), pass it through unchanged.
 * Otherwise append '*' to treat plain text as a prefix match (e.g. "john" → "john*").
 *
 * An empty string intentionally returns '*' (match all customers), which is the correct
 * API behavior for a "show all people" mode. The query is currently gated by
 * isSearchingEnabled (requires non-empty input), so this path is not yet reachable —
 * but '*' is the right value if that gate is ever relaxed.
 */
export function buildPeopleSearchQuery(query: string): string {
  if (query.includes('@') || query.includes('*')) return query;
  return `${query}*`;
}

/**
 * Computes the 1-based position of an item within the command dialog result list.
 *
 * Layout order:
 *   [quick actions]   → 1 + cmdIndex
 *   [local search]    → 1 + hydraCommandsCount + groupOffset + resultIndex
 *   [api results]     → 1 + hydraCommandsCount + localSearchCount + groupOffset + index
 *   [navigation]      → 1 + hydraCommandsCount + localSearchCount + apiResultsCount + index
 *   [ask agent]       → always last
 */
export function computeItemPosition({
  hydraCommandsCount,
  localSearchCount,
  section,
  groupOffset = 0,
  itemIndex,
}: {
  hydraCommandsCount: number;
  localSearchCount: number;
  section: 'localSearch' | 'apiResults';
  groupOffset?: number;
  itemIndex: number;
}): number {
  switch (section) {
    case 'localSearch':
      return 1 + hydraCommandsCount + groupOffset + itemIndex;
    case 'apiResults':
      return 1 + hydraCommandsCount + localSearchCount + groupOffset + itemIndex;
    default: {
      const _exhaustive: never = section;
      return _exhaustive;
    }
  }
}

// Covers all navigation entries currently indexed by Vertex (Discovery Engine).
// Will be replaced when navigation results move to FE-generated local data.
// Ordered most-specific first so the first match wins.
const NAVIGATION_ICON_PATTERNS: [string, IconName][] = [
  // Broadcasts (specific sub-routes before generic)
  ['broadcasts/api-triggered', 'api-triggered-broadcast'],
  ['broadcasts/newsletter', 'newsletter'],
  ['/broadcasts', 'broadcast'],
  // Segments
  ['segments/explore', 'segment-data-driven'],
  ['/segments', 'segment'],
  // Messaging
  ['/campaigns', 'campaigns'],
  ['/transactional', 'transactional-api'],
  ['/anonymous', 'anonymous'],
  ['/outbox', 'delivery-log'],
  // Audience
  ['/people', 'person'],
  ['/objects', 'object'],
  ['/goals', 'goal'],
  // Review
  ['/activity_logs', 'activity'],
  ['/analysis', 'analysis'],
  ['/health', 'analysis'],
  // Content
  ['message-library', 'folder'],
  ['/assets', 'image'],
  ['/layouts', 'layout'],
  ['/snippets', 'liquid'],
  // Data
  ['data-index', 'data-index'],
  ['integrations/reporting-webhooks', 'webhook'],
  ['/integrations', 'integrations'],
  ['/imports', 'import'],
  ['/exports', 'export'],
  ['/collections', 'database'],
  // Design Studio
  ['design-studio', 'design-studio-new'],
  // Agent / AI
  ['/agent', 'sparkle'],
  // Pipelines
  ['/pipelines', 'data-index'],
  // Settings – actions (channel-specific before generic)
  ['actions/email', 'email'],
  ['actions/push', 'mobile'],
  ['actions/in_app', 'in-app'],
  ['actions/slack', 'slack'],
  ['actions/sms', 'sms'],
  ['actions/twilio', 'sms'],
  ['actions/whatsapp', 'brand/whatsapp'],
  ['actions/messaging_limit', 'message-limit'],
  ['actions/time_zone_match', 'time-window'],
  ['actions/url_params', 'link'],
  // Settings – workspace-specific
  ['settings/ai-business-profile', 'sparkle'],
  ['settings/llm_configuration', 'sparkle'],
  ['settings/api_credentials', 'api-credentials'],
  ['settings/api-keys', 'api-credentials'],
  ['settings/webhook_configuration', 'webhook'],
  ['settings/subscription_center', 'email'],
  ['settings/merge_options', 'merge'],
  ['settings/suppressions', 'blocks'],
  ['settings/audit-logs', 'audit'],
  // Settings – account-level
  ['/settings/security', 'lock-secure'],
  ['/settings/team', 'manage-team'],
  ['/settings/roles', 'manage-team'],
  ['/settings/billing', 'billing-history'],
  ['/settings/privacy', 'data-privacy'],
  ['/settings/personal', 'person'],
  ['/settings/workspaces', 'workspaces'],
  ['/settings/experimental-features', 'sparkle'],
  ['/settings/account', 'workspace-settings'],
  // Home / Dashboard (after more-specific paths that also contain "home" or "dashboard")
  ['/home', 'home'],
  ['/dashboard', 'home'],
  // Generic settings fallback
  ['/settings', 'workspace-settings'],
];

function getNavigationIcon(url: string): IconName {
  const path = URL.canParse(url) ? new URL(url).pathname : url;
  for (const [pattern, icon] of NAVIGATION_ICON_PATTERNS) {
    if (path.includes(pattern)) return icon;
  }
  return 'arrow-right';
}

export function getIconNameFromSearchResult(result: SearchResult): IconName {
  switch (result.kind) {
    case 'campaign':
      return CAMPAIGN_TYPE_ICONS?.[result.other.campaign_type] ?? 'campaigns';
    case 'template':
      return TEMPLATE_TYPE_ICONS?.[result.other.template_type] ?? 'layout';
    case 'segment':
      return result.other.segment_type === 'static' ? 'segment-manual' : 'segment-data-driven';
    case 'action':
      return ACTION_TYPE_ICONS?.[result.other.action_type] ?? 'help';
    case 'newsletter':
      return 'newsletter';
    case 'goal':
      return 'goal';
    case 'navigation':
      return getNavigationIcon(result.url);
    case 'customer':
      return 'person';
    case 'docs':
      return 'connect-file';
    case 'source':
      return 'data-index';
    case 'destination':
      return 'integrations';
    default:
      return 'search';
  }
}

function groupTemplatesByType(results: SearchResult[]) {
  const templateTypes: TemplateType[] = [];

  const groupedByTemplateType = results.reduce((groups, item) => {
    if (item.kind !== 'template') {
      return groups;
    }

    const templateType = item.other.template_type;

    if (groups[templateType] == null) {
      templateTypes.push(templateType);
      groups[templateType] = [];
    }

    groups[templateType].push(item);

    return groups;
  }, {} as Record<TemplateType, SearchResult[]>);

  return { groupedByTemplateType, templateTypes };
}

/**
 * The backend skips indexing templates with template_type "unset", so in practice
 * this remap should never fire for newly indexed content. It exists to handle any
 * "unset" templates that were already in the index before that skip was introduced.
 * If they do appear, remap them to kind "newsletter" so they group and render correctly.
 */
export function remapUnsetTemplates(results: SearchResult[]): SearchResult[] {
  return results.map((r) =>
    r.kind === 'template' && r.other.template_type === 'unset'
      ? ({
          ...r,
          kind: 'newsletter' as const,
          other: {
            ...r.other,
            id: r.other.newsletter_id ?? r.other.id,
            sent: 'false',
          },
        } satisfies SearchResult)
      : r,
  );
}

export function groupSearchResults(results: SearchResult[]) {
  // We exclude 'template' and 'campaign' from the group type as they are split into sub-groups.
  // Templates are grouped by template_type, and campaigns split out 'triggered_broadcast' as its own group.
  // First iteration preserves API order
  const resultsByKind = results.reduce((groups, item) => {
    const kind = item.kind;
    groups[kind] ??= [];
    groups[kind].push(item);
    return groups;
  }, {} as Record<SearchResult['kind'], SearchResult[]>);

  const entries: [GroupType, SearchResult[]][] = [];

  for (const [kind, items] of Object.entries(resultsByKind) as [SearchResult['kind'], SearchResult[]][]) {
    if (kind === 'template') {
      // Second pass: split template group by template_type; order = first occurrence in template list.
      const { groupedByTemplateType, templateTypes } = groupTemplatesByType(items);

      for (const t of templateTypes) {
        if (t === 'unset') continue;
        entries.push([t, groupedByTemplateType[t]]);
      }
    } else if (kind === 'campaign') {
      // Split triggered_broadcast campaigns into their own group.
      const campaigns: SearchResult[] = [];
      const triggeredBroadcasts: SearchResult[] = [];

      for (const item of items) {
        if (item.kind === 'campaign' && item.other.campaign_type === 'triggered_broadcast') {
          triggeredBroadcasts.push(item);
        } else {
          campaigns.push(item);
        }
      }

      if (campaigns.length > 0) {
        entries.push(['campaign', campaigns]);
      }
      if (triggeredBroadcasts.length > 0) {
        entries.push(['triggered_broadcast', triggeredBroadcasts]);
      }
    } else {
      entries.push([kind as GroupType, items]);
    }
  }

  return entries;
}

// Matches a full valid email OR a domain-suffix search like "@example.com" / "user@example.com"
// where at least one dot follows the @. Used to prioritize the people endpoint in results.
const EMAIL_SUFFIX_RE = /^.*@[^\s@]+\.[^\s@]+$/;
export function isEmailQuery(value: string): boolean {
  return z.email().safeParse(value).success || EMAIL_SUFFIX_RE.test(value);
}

// Chosen to block scattered false positives (e.g. "campai" → dark mode scores ~0.0001)
// while still passing abbreviation-style queries (e.g. "drk" → dark mode scores ~0.15).
// Uses cmdk's defaultFilter (commandScore) — https://github.com/dip/cmdk/blob/v1.1.1/cmdk/src/command-score.ts
const LOCAL_COMMAND_SCORE_THRESHOLD = 0.1;

export function matchesLocalCommand(query: string): (cmd: LocalCommand) => boolean {
  const trimmedQuery = query.trim();
  return (cmd) => {
    if (trimmedQuery.length === 0) return true;
    const haystack = [cmd.label, cmd.description].filter(Boolean).join(' ');
    return defaultFilter(haystack, trimmedQuery, cmd.keywords ?? []) >= LOCAL_COMMAND_SCORE_THRESHOLD;
  };
}
